<template>
	<div id="cam"></div>
	<WeldlineDisplay></WeldlineDisplay>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted } from "vue";
import * as THREE from "three";
import { ViewportGizmo } from "three-viewport-gizmo";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";
import { useMainStore } from "@/store/mainStore";
import { FeatureType } from "@/model/FeatureType";
import { EditorMode } from "@/model/Enums";
import WeldlineDisplay from "./WeldlineDisplay.vue";
import { BlexonColor } from "@/model/BlexonColor";
import { acceleratedRaycast } from "three-mesh-bvh";
import { useI18n } from "vue-i18n";

// Add the extension functions
THREE.Mesh.prototype.raycast = acceleratedRaycast;

const props = defineProps<{ fitCameraToObjectToggle: boolean }>();

const { t } = useI18n();
const store = useMainStore();
const mousedown = ref(false);

// Colors
const sceneBackgroundColor: THREE.Color = new THREE.Color(0xf1f1f1);

// Vectors
const mouse = new THREE.Vector2();
let raycaster: THREE.Raycaster = new THREE.Raycaster();

// Scene & Camera - can be null initially
let scene = new THREE.Scene();
let camera = new THREE.OrthographicCamera(0, 0, 0, 0, -10000, 10000);
let controls: TrackballControls | null = null;
const baseRotateSpeed = 8;
const minRotateSpeed = 0.1;
const maxRotateSpeed = 20;
let boundingRadius = 0;

// Renderer
let renderer: THREE.WebGLRenderer | null = null;
// Gizmo
let gizmo: ViewportGizmo | null = null;

// Interaction states
const zoomingPanning = ref(false);
const moveThreshold = 3; // Amount of pixel movement below which a click/touch is still registered
let clickStartX = 0,
	clickStartY = 0;

// Timeout for resize event
let resizeTimeout: NodeJS.Timeout;
// Timeout for zoom event
let zoomTimeout: NodeJS.Timeout;

// Rotate object automatically
const rotationSpeeds = [10000, 5000, 2500]; // Smaller rotates faster
let isRotating = false;
let rotationSpeed = rotationSpeeds[0];
let rotationAnimationFrameId: number | null = null;

watch(store.selectedFeatures, () => {
	store.cam.selectAndColorByExportId(store.selectedFeatures.flatMap((f) => f.exportIds));
});

watch(store.selectedFeatures, () => {
	const rootGroup = store.cam.sceneGroup;
	if (!rootGroup) {
		return;
	}
	const selectedEngravings = store.selectedFeatures.filter(
		(f) => f.featureType === FeatureType.Engraving
	);
	const material = new THREE.MeshBasicMaterial();
	material.color = new THREE.Color(BlexonColor.selectionColor);
	const engravings = rootGroup.children.filter((c) => c.name == "Engraving");
	rootGroup.remove(...engravings);

	for (const engraving of selectedEngravings) {
		var edges = store.cam.allEdges.filter((e) => engraving.exportIds.includes(e.id));
		for (const edge of edges) {
			const points: THREE.Vector3[] = [];
			for (const point of edge.points) {
				points.push(new THREE.Vector3(point.x, point.y, point.z));
			}

			const pipeSpline = new THREE.CatmullRomCurve3(points, false, "catmullrom", 0);
			const geometry = new THREE.TubeGeometry(pipeSpline, 64, 0.1, 8, false);
			const engravingMesh = new THREE.Mesh(geometry, material);
			engravingMesh.name = "Engraving";
			rootGroup!.add(engravingMesh);
		}
	}
});

// Why is this first parameter not simply the prop itself?
// Because "watch" expects a reactive object to watch and not a primitive, which fitCameraToObjectToggle is.
// By changing it to () => props.fit... it becomes a computed property and is reactive.
// The value of the property doesn't matter. Any change should trigger the function.
watch(
	() => props.fitCameraToObjectToggle,
	() => {
		fitCameraToObject();
	}
);

watch(
	() => store.cam.displayedBodies,
	() => {
		setupScene(false);
	}
);

watch(
	() => store.renderGizmo,
	() => {
		if (camera) {
			// The objects needs to do SOMETHING so that it is rerendered.
			// I've tried just forcing a re-render with render() on gizmo and renderer
			// but it did nothing. This works and you can't see it.
			camera.zoom = camera.zoom * 1.0001;
		}
	}
);

watch(
	() => store.rotateObjectState,
	() => {
		if (store.rotateObjectState !== 0) {
			// stop angoing rotation
			stopRotation();
			// adjust rotation speed
			rotationSpeed = rotationSpeeds[store.rotateObjectState - 1];
			// start new rotation
			startRotation();
		} else {
			stopRotation();
			// reset the speed for the next rotation
			rotationSpeed = rotationSpeeds[0];
		}
	}
);

const init = async () => {
	addEventListeners();

	// Initialize scene with lighting
	scene.background = sceneBackgroundColor;
	const light = new THREE.AmbientLight(0xffffff);
	scene.add(light);
	setupScene(false);

	boundingRadius = getSizeOfScene().boundingRadius;

	// Initialize the camera
	setupCamera();

	// 3D Controls initialisieren
	controls = new TrackballControls(camera, document.getElementById("cam")!);
	// Pan speed is relational to the size of the article. 1450 is a magic number that should make it feel similar
	// no matter how large the article is.
	const radius = Math.max(store.cam.model.height, store.cam.model.width, store.cam.model.length);
	controls.panSpeed = 1450 / radius;
	controls.noZoom = !store.isTouchscreen; // Disable Zoom when NOT on touchscreen
	controls.staticMoving = true;

	// // Beim Zoomen oder Verschieben kann nicht gleichzeitig das Hovering überprüft werden.
	// // Es wird deshalb für 0.1 Sekunden ausgesetzt
	controls.addEventListener("start", () => {
		clearTimeout(zoomTimeout);
		zoomingPanning.value = true;
	});
	controls.addEventListener("end", () => {
		clearTimeout(zoomTimeout);
		zoomingPanning.value = false;
	});

	// Initialize renderer
	setupRenderer();

	// Setup the 3D cube
	setupGizmo(renderer!);

	// Configure raycaster

	// Important, if "Line" or "LineSegments" is used for lines instead of meshes.
	// Otherwise the hitbox for the line is quite large.
	// The smaller this value, the smaller the hitbox for the lines.
	// raycaster!.params!.Line!.threshold = 0.3;

	// Optimizes the raycaster.
	raycaster.firstHitOnly = true;

	fitCameraToObject();

	startRendering();
};

const setupRenderer = () => {
	renderer = new THREE.WebGLRenderer({ antialias: true });
	renderer.setSize(window.innerWidth, window.innerHeight);
	renderer.setPixelRatio(window.devicePixelRatio);
	renderer.setClearColor(0x000000, 0); // reduce overdraw
	// Ensure renderer is correctly attached
	const camContainer = document.getElementById("cam");
	if (camContainer && !camContainer.contains(renderer.domElement)) {
		camContainer.innerHTML = ""; // Clear old elements
		camContainer.appendChild(renderer.domElement);
	}
};

const setupGizmo = (newRenderer: THREE.WebGLRenderer) => {
	if (gizmo) {
		gizmo.dispose(); // Clean up existing gizmo
		document.getElementById("gizmo")?.remove(); // Remove HTML element
	}
	const hover = { color: BlexonColor.hoveredGeometryColor };
	gizmo = new ViewportGizmo(camera, newRenderer, {
		type: "cube",
		placement: "bottom-left",
		right: { label: t("panels.cadView.gizmoRight"), hover: hover },
		left: { label: t("panels.cadView.gizmoLeft"), hover: hover },
		top: { label: t("panels.cadView.gizmoTop"), hover: hover },
		bottom: { label: t("panels.cadView.gizmoBottom"), hover: hover },
		front: { label: t("panels.cadView.gizmoFront"), hover: hover },
		back: { label: t("panels.cadView.gizmoBack"), hover: hover },
		edges: { hover: hover, opacity: 1 },
		corners: { hover: hover },
		container: "#cam",
		id: "gizmo",
	});
	gizmo.attachControls(controls);
	// TrackballControls allows free rolling and modifies camera.up while rotating.
	// At the end of every controls.update() it calls camera.lookAt(target), which uses
	// camera.up to determine the roll. So after a free rotation the up vector is tilted
	// and the gizmo's straight target orientation gets re-tilted on the next frame.
	//
	// On "start" we reset to the world up so the transition animation for the side views
	// stays straight.
	gizmo.addEventListener("start", () => {
		camera.up.set(0, 1, 0);
	});
	// On "end" we pick a clean camera.up deterministically from the viewing axis.
	// After a gizmo view change the camera looks along a world axis. Deriving the up vector
	// from the camera orientation is ill-conditioned near the top/bottom pole (the animation
	// ends with a small residual off the exact axis), which leaves the view tilted on the
	// first click. Instead we choose the up directly from the view direction: the world up,
	// unless we look along Y, where the world up would be parallel to the view direction
	// (degenerate) - then use ±Z to match the gizmo's top/bottom convention.
	gizmo.addEventListener("end", () => {
		const dir = camera.position.clone().sub(controls!.target).normalize();
		if (Math.abs(dir.y) > Math.abs(dir.x) && Math.abs(dir.y) > Math.abs(dir.z)) {
			// Looking along the Y axis (top or bottom).
			camera.up.set(0, 0, -Math.sign(dir.y));
		} else {
			camera.up.set(0, 1, 0);
		}
	});
	// Because the Gizmo-HTML is rendered dynamically, normal
	// CSS doesn't work. It's loaded too soon.
	// Setting to 2: higher than CAM but lower than panels.
	document.getElementById("gizmo")!.style.zIndex = "2";
};

const startRendering = async () => {
	// Show
	const animate = () => {
		requestAnimationFrame(animate);
		controls!.update();
		renderer!.render(scene, camera);
		gizmo!.render();
	};

	animate();
};

const setupCamera = () => {
	camera.left = window.innerWidth / -2;
	camera.right = window.innerWidth / 2;
	camera.top = window.innerHeight / 2;
	camera.bottom = window.innerHeight / -2;

	camera.updateProjectionMatrix();
};

const setupScene = (fitCamera: boolean) => {
	if (scene.children.length === 1) {
		const group = new THREE.Group();
		//group.rotation.x = -Math.PI*.5;
		const transformation = store.cam.model.transformationMatrix;
		const translation = new THREE.Vector3(
			-transformation[0],
			-transformation[1],
			-transformation[2]
		);
		group.position.add(translation);
		store.cam.sceneGroup = group;
		scene.add(group);
	}
	const rootGroup = scene.children[1];
	rootGroup.clear();

	// Setup Mesh
	var meshes = store.cam.getMeshes();
	rootGroup.add(...meshes);

	// var contour = store.cam.getContourAsMesh();
	var contour = store.cam.getContourAsMesh();
	rootGroup.add(...contour);

	if (fitCamera) {
		fitCameraToObject();
	}

	scene.updateMatrix();
};

// Rotation speed is dependent on zoom factor.
// But is always set between 1 and 20.
const getRotateSpeed = (): number => {
	if (!camera || !store.cam.sceneGroup) return baseRotateSpeed;

	const zoom = camera.zoom;

	// a * zoom**b + c * radius
	// [ 9.13537737 -2.20172573e-1 -2.43568688e-3]
	const rawTargetRotateSpeed =
		9.13537737 * Math.pow(zoom, -2.20172573e-1) + -2.43568688e-3 * boundingRadius;

	// Backup strategies
	// a * zoom + b * radius + c
	// [-3.36357460e-2 -1.19660211e-3  6.81812146]
	// const rawTargetRotateSpeed = -3.3635746e-2 * zoom + -1.19660211e-3 * boundingRadius + 6.81812146;
	// a * np.log(zoom + 1) + b * radius + c
	// [-1.24620603e+00 -1.61558384e-03  9.02986601e+00]
	// const rawTargetRotateSpeed = -1.24620603 * Math.log(zoom + 1) + -1.61558384e-3 * boundingRadius + 9.02986601;

	const targetRotateSpeed = Math.min(
		maxRotateSpeed,
		Math.max(minRotateSpeed, rawTargetRotateSpeed)
	);

	return targetRotateSpeed;
};

function onMouseDown(event: any) {
	mousedown.value = true;
	clickStartX = event.clientX;
	clickStartY = event.clientY;
}
function onMouseUp() {
	// Only if the mouse was not moved between mousedown and mouseup
	// There is a threshold defined. This is calculated in the mouse move event.
	if (!mousedown.value) return;
	mousedown.value = false;
	store.cam.addHoverToSelected();
}

/**
 * Eventhandler for mouse move. Highlights hovered lines and faces.
 */
function onMouseMove(event: any) {
	// If the mouse is currently clicked, it is checked how much the mouse moved
	// from the position where the button was clicked.
	if (mousedown.value) {
		const dx = Math.abs(event.clientX - clickStartX);
		const dy = Math.abs(event.clientY - clickStartY);
		if (dx >= moveThreshold && dy >= moveThreshold) {
			// If the mouse moved too much, the mouse click is abandoned.
			mousedown.value = false;
		}
	}

	if (zoomingPanning.value) {
		// Wenn gezoomt wird, kann man nicht auch noch raycasting machen.
		// Wird zu langsam
		return;
	}

	const sceneGroup = store.cam.sceneGroup;
	if (!sceneGroup || !sceneGroup.children) {
		return;
	}

	mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
	mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
	// console.log({ clientX: event.clientX, clientY: event.clientY, width: window.innerWidth,
	//     height: window.innerHeight, x: mouse.x, y: mouse.y, camLeft: camera.left, camRight: camera.right });
	raycaster.setFromCamera(mouse, camera);

	// const edgeChildren = sceneGroup.children.filter(c => c.type === "Line2");
	// const faceChildren = sceneGroup.children.filter(c => c.type === "Mesh");
	// const pIntersectStart = performance.now();
	const intersectedObjects = raycaster.intersectObjects(sceneGroup.children);
	// const pIntersectEnd = performance.now();
	// console.log(`intersect: ${(pIntersectEnd - pIntersectStart).toFixed(0)}`);

	if (intersectedObjects.length === 0) {
		// Es wird über gar nichts gehovert
		store.cam.updateHoverColors(-1);
		return;
	}

	let intersectedObject = intersectedObjects[0]; // automatically sorted by distance
	let typeOfIntersected = (intersectedObject.object as any).isLine2 ? "edge" : "face";
	// const typeOfIntersected = intersectedObject.object.type === "Line" ? "edge" : "face";

	if (intersectedObject.object.name === "Weldline") {
		const weldline = store.weldlines.find(
			(w) => w.weldlineId === intersectedObject.object.userData.id
		);
		if (weldline) {
			weldline.hovered = true;
		}
		store.cam.updateHoverColors(-1);
		return;
	} else {
		store.weldlines.forEach((w) => (w.hovered = false));
	}

	// prioritize edges over faces if they're close together
	const first3 = intersectedObjects.slice(0, 2);
	if (
		typeOfIntersected !== "edge" &&
		canSelectLine() &&
		first3.some((o) => (o.object as any).isLine2)
	) {
		const firstEdge = first3.filter((o) => (o.object as any).isLine2)[0];
		if (Math.abs(intersectedObject.distance - firstEdge.distance) < 0.5) {
			intersectedObject = firstEdge;
			typeOfIntersected = "edge";
		}
	}

	if (
		(typeOfIntersected === "edge" && canSelectLine()) ||
		(typeOfIntersected === "face" && canSelectFace())
	) {
		const faceOrEdge = intersectedObject.object.userData;
		if (faceOrEdge) {
			store.cam.updateHoverColors(faceOrEdge.groupId, typeOfIntersected);
			return;
		}
	}
}

const canSelectLine = () => {
	return (
		store.editorMode === EditorMode.FeatureMode ||
		store.editorMode === EditorMode.MeasuringMode ||
		(store.editorMode === EditorMode.RollDirectionMode && !store.article.isReadOnly)
	);
};

const canSelectFace = () => {
	return (
		store.editorMode === EditorMode.FeatureMode ||
		store.editorMode === EditorMode.MeasuringMode ||
		store.editorMode === EditorMode.WeldMode
	);
};

const getSizeOfScene = (): {
	box: THREE.Box3;
	boundingSize: THREE.Vector3;
	boundingRadius: number;
} => {
	// Calculate appropriate zoom value via bounding box radius
	// This is quite complicated because we need to know how the model is
	// located in projected space. We can't rely on simple bounding box values.
	var box = new THREE.Box3().setFromObject(store.cam.sceneGroup!);
	const boundingSize = new THREE.Vector3();
	box.getSize(boundingSize);
	const boundingSphere = new THREE.Sphere();
	box.getBoundingSphere(boundingSphere);
	const boundingRadius = boundingSphere.radius;
	return { box, boundingSize, boundingRadius };
};

function fitCameraToObject() {
	if (!store.cam.sceneGroup) {
		return;
	}
	controls!.reset();

	// Position the camera so that it looks at the model in an angle
	const model = store.cam.model;
	const maxDimension = Math.max(model.height, model.width, model.length);
	const angleX = THREE.MathUtils.degToRad(35); // Convert to radians
	const angleY = THREE.MathUtils.degToRad(45);
	const xPosition = maxDimension * Math.cos(angleY);
	const yPosition = maxDimension * Math.sin(angleX);
	const zPosition = maxDimension * Math.sin(angleY);
	camera.position.set(
		xPosition, // X
		yPosition, // Y
		zPosition // Z
	);

	// Ensure the camera looks at the center
	camera.lookAt(0, 0, 0);

	const sizes = getSizeOfScene();
	const box = sizes.box;

	// Get the 8 corners of the bounding box
	const corners = [
		new THREE.Vector3(box.min.x, box.min.y, box.min.z),
		new THREE.Vector3(box.min.x, box.min.y, box.max.z),
		new THREE.Vector3(box.min.x, box.max.y, box.min.z),
		new THREE.Vector3(box.min.x, box.max.y, box.max.z),
		new THREE.Vector3(box.max.x, box.min.y, box.min.z),
		new THREE.Vector3(box.max.x, box.min.y, box.max.z),
		new THREE.Vector3(box.max.x, box.max.y, box.min.z),
		new THREE.Vector3(box.max.x, box.max.y, box.max.z),
	];
	// Project each corner into normalized screen space
	const projectedCorners = corners.map((corner) => corner.project(camera));

	// Find min and max screen-space coordinates
	let minX = Infinity,
		maxX = -Infinity,
		minY = Infinity,
		maxY = -Infinity;
	projectedCorners.forEach((p) => {
		minX = Math.min(minX, p.x);
		maxX = Math.max(maxX, p.x);
		minY = Math.min(minY, p.y);
		maxY = Math.max(maxY, p.y);
	});

	if (sizes.boundingRadius * 2 > 2000) {
		// 2000 is the default value of far
		camera.far = sizes.boundingRadius * 4; // 4 ist eher ein willkürlicher Wert mit dem ich probiert habe.
	}

	// Compute the on-screen bounding box size
	const screenWidth = (maxX - minX) * window.innerWidth;
	const screenHeight = (maxY - minY) * window.innerHeight;
	// Compute zoom with margin for adjustment
	const zoomX = window.innerWidth / (screenWidth * 1.2);
	const zoomY = window.innerHeight / (screenHeight * 1.2);
	const zoomRadiusX = window.innerWidth / (sizes.boundingRadius * 1.2);
	const zoomRadiusY = window.innerHeight / (sizes.boundingRadius * 1.2);
	const zoom = Math.min(zoomX, zoomY, zoomRadiusX, zoomRadiusY);
	camera.zoom = zoom;
	store.zoom = zoom; // Initial setting
	camera.updateProjectionMatrix();
	camera.updateMatrix();
	controls!.rotateSpeed = getRotateSpeed();
	controls!.update();
}

// When zooming, halt raycasting for .5 seconds.
function handleZoom() {
	zoomingPanning.value = true;
	clearTimeout(zoomTimeout);
	zoomTimeout = setTimeout(() => {
		zoomingPanning.value = false;
	}, 250);
}

function onMouseWheel(event: any) {
	event.preventDefault();
	event.stopPropagation();

	handleZoom();

	// When the scroll wheel is used, native Trackball zooming is disabled
	if (!controls!.noZoom) {
		controls!.noZoom = true;
	}
	var factor = 1.1;
	if (event.deltaY < 0) {
		factor = 0.9;
	}

	const mX = (event.offsetX / window.innerWidth) * 2 - 1;
	const mY = -(event.offsetY / window.innerHeight) * 2 + 1;
	const vector1 = new THREE.Vector3(mX, mY, 0);
	const vector2 = new THREE.Vector3(0, 0, 0);
	vector1.unproject(camera);
	vector2.unproject(camera);
	vector1.subVectors(vector1, vector2);

	camera.zoom /= factor;
	store.zoom = camera.zoom;
	vector1.multiplyScalar(factor - 1.0);
	camera.position.subVectors(camera.position, vector1);
	controls!.target.subVectors(controls!.target, vector1);
	controls!.rotateSpeed = getRotateSpeed();
	controls!.update();
	camera.updateProjectionMatrix();
	camera.updateMatrix();
}

function onTouchGesture() {
	// When a touch gesture is recognized, the Trackball zoom is enabled
	if (controls!.noZoom) {
		controls!.noZoom = false;
	}
}

function onResize() {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(() => {
		setupCamera();
		controls!.handleResize();
		controls!.update();

		// Initialize the renderer again.
		setupRenderer();
		// Gizmo needs to be setup again because it was attached to the renderer.
		setupGizmo(renderer!);
	}, 200);
}

const addEventListeners = () => {
	const cadContainer = document.querySelector("#cam");
	if (!cadContainer) return;
	// Resize window -> adjust camera
	window.addEventListener("resize", onResize, false);
	// Click => add feature to selection if hovering over it
	cadContainer.addEventListener("mousedown", onMouseDown, false);
	cadContainer.addEventListener("mouseup", onMouseUp, false);
	// Highlight hovered lines on mouse move
	cadContainer.addEventListener("mousemove", onMouseMove, false);
	// Zooming works natively with the Trackball-Controls on touch devices
	// Zoom has to be handled with custom controls on a desktop (without touchscreen)
	// Because otherwise it won't zoom into the mouse cursor
	cadContainer.addEventListener("wheel", onMouseWheel, false);
	cadContainer.addEventListener("touchstart", onTouchGesture, false);
	cadContainer.addEventListener("touchmove", onTouchGesture, false);
	cadContainer.addEventListener("touchend", onTouchGesture, false);
};

const removeEventListeners = () => {
	const cadContainer = document.querySelector("#cam");
	if (!cadContainer) return;
	window.addEventListener("resize", onResize, false);
	// Click => add feature to selection if hovering over it
	cadContainer.removeEventListener("mousedown", onMouseDown, false);
	cadContainer.removeEventListener("mouseup", onMouseUp, false);
	// Highlight hovered lines on mouse move
	cadContainer.removeEventListener("mousemove", onMouseMove, false);
	cadContainer.removeEventListener("wheel", onMouseWheel, false);
	cadContainer.removeEventListener("touchstart", onTouchGesture, false);
	cadContainer.removeEventListener("touchmove", onTouchGesture, false);
	cadContainer.removeEventListener("touchend", onTouchGesture, false);
};

const startRotation = () => {
	if (!isRotating) {
		isRotating = true;
		rotate(0, 0);
	}
};
const stopRotation = () => {
	if (rotationAnimationFrameId) {
		cancelAnimationFrame(rotationAnimationFrameId);
		rotationAnimationFrameId = null;
		isRotating = false;
	}
};

const rotate = (degrees: number, time: number) => {
	const rotationStep = (360 * time) / rotationSpeed;

	const box = new THREE.Box3().setFromObject(store.cam.sceneGroup!);
	const target = new THREE.Vector3();
	box.getCenter(target);
	const angleRad = THREE.MathUtils.degToRad(rotationStep - degrees);
	degrees = rotationStep;

	const position = camera.position;

	// put position on target
	const positionRelative = position.clone().sub(target);
	const rotationMatrix = new THREE.Matrix4().makeRotationAxis(camera.up, angleRad);
	// rotate position in the center of target
	const rotatedPositionRelative = positionRelative.applyMatrix4(rotationMatrix);
	// put back position
	const rotatedPosition = rotatedPositionRelative.add(target);

	camera.position.fromArray(rotatedPosition.toArray());
	camera.updateProjectionMatrix();
	controls!.update();
	// call recursively
	rotationAnimationFrameId = requestAnimationFrame((time) => rotate(degrees, time));
};

// This updates the center of rotation so that the object is always rotated around its center.
// But with trackball controls, this means that we must always look at the center of the object.
// So panning is not possible. The camera always snaps to the center again.
// function updateRotationCenter() {
//     if (!store.cam.sceneGroup || !cameraControls.value) return;

//     const center = new THREE.Vector3();
//     store.cam.sceneGroup.getWorldPosition(center); // Get object's world position
//     cameraControls.value!.target.copy(center); // Set rotation target to object's center
// }

onMounted(init);
onUnmounted(removeEventListeners);
</script>

<style scoped>
/* 3D visuals container */
#cam {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: -10;
}
</style>
